// src/context/ManagerContext.jsx
//
// Focused context for manager selection state and manager-derived data.
// Extracts the selectedManager/selectManager/getManagers/getManagerData/isPaidPlayer
// slice from the old DataContext god-hook.
//
import React, { createContext, useState, useContext, useMemo, useCallback } from 'react';
import { useLeagueData } from './LeagueDataContext';

// Minimum actual_points for a captain pick to be considered "successful"
const CAPTAIN_SUCCESS_THRESHOLD = 8;

const ManagerContext = createContext(null);

/**
 * Hook to access the currently selected manager and all manager-derived data.
 * Use this instead of useData() when a component only needs manager identity,
 * selection helpers, or pre-computed manager metrics.
 */
export const useManagerContext = () => {
  const context = useContext(ManagerContext);
  if (!context) {
    throw new Error('useManagerContext must be used within a ManagerProvider');
  }
  return context;
};

/**
 * Provides manager selection state and all per-manager derived data.
 * Depends on LeagueDataProvider being present above it in the tree.
 */
export const ManagerProvider = ({ children }) => {
  // Consume raw league data from the focused LeagueDataContext —
  // this avoids re-subscribing to the full god-context value.
  const { data } = useLeagueData();

  const [selectedManager, setSelectedManager] = useState(null);

  // Update selected manager — exposed as a stable callback reference
  const selectManager = useCallback((managerName) => {
    setSelectedManager(managerName);
  }, []);

  // Index: de-duplicated manager name list, computed once per data change so
  // getManagers() returns a stable reference rather than rebuilding on every call.
  const managersIndex = useMemo(() => {
    if (!data?.weeklyPoints) return [];
    return [...new Set(data.weeklyPoints.map(row => row.Manager))];
  }, [data]);

  // Index: paid-players Set, computed once per data change so isPaidPlayer()
  // performs an O(1) Set lookup rather than an O(n) array scan.
  const paidPlayersSet = useMemo(() => {
    if (!data?.['Paid Players']) return new Set();
    return new Set(data['Paid Players'].map(player => player.Manager));
  }, [data]);

  const getManagers = useCallback(() => managersIndex, [managersIndex]);

  const isPaidPlayer = useCallback(
    (manager) => !!manager && paidPlayersSet.has(manager),
    [paidPlayersSet]
  );

  // Compute all manager-specific derived metrics in one memoised pass.
  // Re-runs only when the raw data or the selected manager changes.
  const managerData = useMemo(() => {
    if (!data || !selectedManager) return null;

    // Weekly points row for this manager
    const managerWeeklyData = data.weeklyPoints?.find(
      row => row.Manager === selectedManager && row.Total !== undefined
    );

    const totalPoints = managerWeeklyData?.Total || 0;

    // Derive ordinal league position from a sorted snapshot
    const sortedManagers = [...(data.weeklyPoints || [])]
      .filter(row => row.Total !== undefined)
      .sort((a, b) => b.Total - a.Total);

    const position = sortedManagers.findIndex(row => row.Manager === selectedManager) + 1;
    const leaguePosition =
      position === 1 ? '1st' :
      position === 2 ? '2nd' :
      position === 3 ? '3rd' :
      `${position}th`;

    // Loyalty issues (transfer regrets)
    const loyaltySummary = data.loyaltyIssues?.summary?.find(
      row => row.Manager === selectedManager
    );
    const loyaltyDetails = data.loyaltyIssues?.details?.filter(
      row => row.Manager === selectedManager
    ) || [];

    // Captain hindsight
    const captainSummary = data.captainHindsight?.summary?.find(
      row => row.Manager === selectedManager
    );
    const captainDetails = data.captainHindsight?.details?.filter(
      row => row.Manager === selectedManager
    ) || [];

    // Captain success rate uses the module-level threshold constant
    const totalCaptains = captainDetails.length;
    const successfulCaptains = captainDetails.filter(
      row => row.actual_points >= CAPTAIN_SUCCESS_THRESHOLD
    ).length;

    // Differential players
    const differentialSummary = data.differentialPlayers?.summary?.find(
      row => row.Manager === selectedManager
    );
    const differentialDetails = data.differentialPlayers?.details?.filter(
      row => row.Manager === selectedManager
    ) || [];

    // Half-season comparison
    const seasonComparison = data.halfSeasonComparison?.find(
      row => row.Manager === selectedManager
    );

    // Formations
    const formationData = data.formations?.find(
      row => row.Manager === selectedManager
    );

    // Bench points
    const benchData = data.benchPoints?.find(
      row => row.Manager === selectedManager
    );

    // Aggregate points missed across loyalty and captain categories
    const pointsMissed =
      (loyaltySummary?.Total_Points_Missed || 0) +
      (captainSummary?.Points_Missed || 0);

    return {
      name: selectedManager,
      totalPoints,
      leaguePosition,
      loyaltyIssues: { summary: loyaltySummary, details: loyaltyDetails },
      captainHindsight: { summary: captainSummary, details: captainDetails },
      captainSuccessRate: `${successfulCaptains}/${totalCaptains}`,
      differentialPlayers: { summary: differentialSummary, details: differentialDetails },
      seasonComparison,
      pointsMissed,
      formations: formationData ? {
        mostCommonFormation: formationData.Most_Common_Formation || 'Unknown',
        formationUsagePercent: formationData.Formation_Usage_Percent || 0,
        formationVariety: formationData.Formation_Variety || 0,
        matchesLeagueFormation: formationData.Matches_League_Formation || false,
      } : null,
      benchPoints: benchData?.Points_on_Bench || 0,
      benchHaunting: data.benchHaunting?.filter(row => row.Manager === selectedManager) || null,
    };
  }, [data, selectedManager]);

  // Stable callback wrapper around the memoised value —
  // matches the getManagerData() API from the original god-hook.
  const getManagerData = useCallback(() => managerData, [managerData]);

  const value = useMemo(() => ({
    selectedManager,
    selectManager,
    getManagers,
    getManagerData,
    isPaidPlayer,
  }), [selectedManager, selectManager, getManagers, getManagerData, isPaidPlayer]);

  return (
    <ManagerContext.Provider value={value}>
      {children}
    </ManagerContext.Provider>
  );
};

export default ManagerContext;
